// https://emailregex.com/
// Slightly modified to disallow quoted strings
export const EMAIL_REGEX =
  /^([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const cleanEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

/**
 * Cleans and validates an email to check if it is valid
 * @param origEmail the email to clean and validate
 */
export const validateEmail = (origEmail: string): string => {
  const email = cleanEmail(origEmail);
  if (!EMAIL_REGEX.test(email)) {
    throw new Error("Not a valid email");
  }
  return email;
};

/**
 * Remove portion of email alias including and after the "+" up until the "@".
 */
export const stripPlusComponent = (email: string): string => {
  const plusIndex = email.indexOf("+");
  const atIndex = email.lastIndexOf("@");
  return plusIndex >= 0 && plusIndex < atIndex
    ? `${email.slice(0, plusIndex)}${email.slice(atIndex)}`
    : email;
};

// Gmail ignores dots in the local part, so a.b@gmail.com and ab@gmail.com are the
// same mailbox: https://support.google.com/mail/answer/7436150. googlemail.com is a
// legacy alias for those same mailboxes. This does NOT hold for Google Workspace
// custom domains, where dots are significant, so the list stays limited to Gmail's
// own consumer domains.
const DOT_INSENSITIVE_DOMAINS = new Set(["gmail.com", "googlemail.com"]);
const DOT_INSENSITIVE_CANONICAL_DOMAIN = "gmail.com";

/**
 * Canonicalize an email down to the mailbox it actually delivers to, so that a
 * single denial covers every alias variant of that mailbox.
 *
 * Strips the plus component on all domains. On Gmail/Googlemail it additionally
 * strips dots from the local part and folds the domain to gmail.com. Dots are
 * left alone everywhere else.
 */
export const canonicalizeEmailForDenial = (email: string): string => {
  const baseEmail = stripPlusComponent(cleanEmail(email));
  const atIndex = baseEmail.lastIndexOf("@");
  if (atIndex < 0) {
    return baseEmail;
  }
  const localPart = baseEmail.slice(0, atIndex);
  const domain = baseEmail.slice(atIndex + 1);
  if (!DOT_INSENSITIVE_DOMAINS.has(domain)) {
    return baseEmail;
  }
  const dotlessLocalPart = localPart.replaceAll(".", "");
  // A local part of only dots is not a real address; leave it untouched rather
  // than emitting "@gmail.com". validateEmail already rejects these.
  if (dotlessLocalPart === "") {
    return baseEmail;
  }
  return `${dotlessLocalPart}@${DOT_INSENSITIVE_CANONICAL_DOMAIN}`;
};

/**
 * Every form of `email` that should be probed against the email denial table:
 * the plus-stripped literal, so denials recorded before canonicalization keep
 * working, plus the canonical mailbox form. Off Gmail the two coincide and this
 * returns a single value.
 *
 * There is no backfill, so a Gmail denial stored as a dotted literal still only
 * matches that exact spelling (plus its `+` aliases) - it does not cover the
 * mailbox's other dot patterns. Re-denying the mailbox in platform admin stores
 * it canonically and closes the gap.
 */
export const getEmailDenialMatchCandidates = (email: string): string[] => {
  const cleanedEmail = cleanEmail(email);
  const baseEmail = stripPlusComponent(cleanedEmail);
  const canonicalEmail = canonicalizeEmailForDenial(cleanedEmail);
  return canonicalEmail === baseEmail
    ? [baseEmail]
    : [baseEmail, canonicalEmail];
};

/**
 * Normalize an email for plus-alias- and dot-variant-aware matching.
 *
 * Returns the cleaned email, its domain, and its local part with the plus
 * suffix and every dot removed (`null` when nothing but dots remains). Used to
 * find deactivated variants of the same mailbox so neither `user+1@example.com`
 * nor `u.ser@example.com` can rejoin when `user@example.com` (or another
 * variant) is deactivated.
 *
 * Unlike `canonicalizeEmailForDenial`, dots are dropped on every domain: some
 * hosted providers deliver dot variants to the same mailbox, and this is only
 * ever used to block self-serve rejoin against an org's deactivated users. The
 * SQL that applies the same normalization to stored emails lives in
 * `UserRepository.getOrgUserForAuthentication` and must stay in sync. That
 * comparison cannot use an index, so it is intended for org-scoped queries,
 * where it is evaluated as a filter over a single org's users.
 */
export const getEmailPlusAliasMatch = (
  email: string,
): {
  cleanedEmail: string;
  domain: string;
  dotlessLocalPart: string | null;
} => {
  const cleanedEmail = cleanEmail(email);
  const baseEmail = stripPlusComponent(cleanedEmail);
  const atIndex = baseEmail.lastIndexOf("@");
  const localPart = baseEmail.slice(0, atIndex);
  const domain = baseEmail.slice(atIndex + 1);
  const dotlessLocalPart = localPart.replaceAll(".", "");
  return {
    cleanedEmail,
    domain,
    dotlessLocalPart: dotlessLocalPart === "" ? null : dotlessLocalPart,
  };
};

export const SUPPORT_USER_EMAIL = "support@hex.tech";

export const isSupportUserEmail = (email: string): boolean =>
  stripPlusComponent(cleanEmail(email)) === SUPPORT_USER_EMAIL;

export const getEmailDomain = (email: string): string => {
  return email.split("@").pop() ?? "";
};

export const isEduDomain = (domain: string): boolean => {
  // EDU domains can end with .edu (US only) or .edu.[country_code] (e.g. "example.edu.au")
  // Additionally, .ac.[country_code] (e.g. "example.ac.uk") also can point to educational institutions
  // There are other country specific emails that do not follow this pattern so this is not an exhaustive check.
  // To check this, we'd need to include special cases (e.g. .some_university.ca) which this function does not currently do

  const eduRegexp = /\.edu(\.[a-z]{2})?$/;
  const acRegexp = /\.ac\.([a-z]{2})?$/; // @[*].ac points to the Ascension Island, and can be a first level domain

  return eduRegexp.test(domain) || acRegexp.test(domain);
};

// These emails are used for customers that buy us directly through 3rd party vendors.
// We should not allow those organizations to see or update their emails.
export const EMAILS_THAT_SHOULD_NOT_BE_UPDATED = [
  "ar+snowflake@hex.tech",
  "ar+aws@hex.tech",
];
